import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  CheckCircle2, XCircle, AlertTriangle, Clock, User, 
  Shield, EyeIcon, Users, Activity, Brain, MonitorX, 
  ScanFace, Target, Star, MessageSquare, Play, FileText,
  ArrowLeft, Loader2, Download, Video, UserX, Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to convert Google Drive view URL to download URL
const getGoogleDriveDownloadUrl = (url) => {
  if (!url) return null;
  
  // If it's already a download URL, return as is
  if (url.includes('export=download')) return url;
  
  // Extract file ID from various Google Drive URL formats
  let fileId = null;
  
  // Format: https://drive.google.com/file/d/{FILE_ID}/view
  const viewMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (viewMatch) fileId = viewMatch[1];
  
  // Format: https://drive.google.com/open?id={FILE_ID}
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (!fileId && openMatch) fileId = openMatch[1];
  
  // Format: https://drive.google.com/uc?id={FILE_ID}
  const ucMatch = url.match(/\/uc\?.*id=([a-zA-Z0-9_-]+)/);
  if (!fileId && ucMatch) fileId = ucMatch[1];
  
  if (fileId) {
    // Return direct download URL
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
  
  // If we can't parse it, return the original URL
  return url;
};

// Helper function to download video
const downloadVideo = async (url, filename) => {
  try {
    toast.info('Starting download...');
    const downloadUrl = getGoogleDriveDownloadUrl(url);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'interview_recording.webm';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Download started! Check your browser downloads.');
  } catch (error) {
    console.error('Download error:', error);
    toast.error('Failed to start download. Opening in new tab...');
    window.open(url, '_blank');
  }
};

// Analytics Card Component (renamed from Proctoring)
function AnalyticsCard({ icon: Icon, title, value, unit, status, description }) {
  const getStatusColor = () => {
    switch (status) {
      case 'good': return 'from-green-500/20 to-green-600/10 border-green-500/30';
      case 'warning': return 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30';
      case 'critical': return 'from-red-500/20 to-red-600/10 border-red-500/30';
      default: return 'from-slate-500/20 to-slate-600/10 border-slate-500/30';
    }
  };

  const getTextColor = () => {
    switch (status) {
      case 'good': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getIconBg = () => {
    switch (status) {
      case 'good': return 'bg-green-500/20';
      case 'warning': return 'bg-yellow-500/20';
      case 'critical': return 'bg-red-500/20';
      default: return 'bg-gray-200';
    }
  };

  return (
    <div className={`p-4 rounded-xl bg-gradient-to-br ${getStatusColor()} border`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${getIconBg()}`}>
          <Icon className={`w-5 h-5 ${getTextColor()}`} />
        </div>
        <Badge variant="outline" className={getTextColor()}>
          {status === 'good' ? 'Good' : status === 'warning' ? 'Needs Attention' : status === 'critical' ? 'Critical' : 'N/A'}
        </Badge>
      </div>
      <h4 className="text-sm font-medium text-gray-600 mb-1">{title}</h4>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${getTextColor()}`}>{value}</span>
        {unit && <span className="text-sm text-gray-500">{unit}</span>}
      </div>
      {description && <p className="text-xs text-gray-500 mt-2">{description}</p>}
    </div>
  );
}

// Score Circle Component
function ScoreCircle({ score, label, size = 'lg' }) {
  const getColor = () => {
    if (score >= 70) return { stroke: '#22c55e', text: 'text-green-600' };
    if (score >= 50) return { stroke: '#eab308', text: 'text-yellow-600' };
    return { stroke: '#ef4444', text: 'text-red-600' };
  };

  const { stroke, text } = getColor();
  const radius = size === 'lg' ? 45 : 35;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${size === 'lg' ? 'w-28 h-28' : 'w-20 h-20'}`}>
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="#e5e7eb"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke={stroke}
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${size === 'lg' ? 'text-2xl' : 'text-lg'} font-bold ${text}`}>
            {score}
          </span>
          <span className="text-xs text-gray-500">{label}</span>
        </div>
      </div>
    </div>
  );
}

export default function StudentResults() {
  const { resultToken } = useParams();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchResults();
  }, [resultToken]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/results/${resultToken}`);
      setInterview(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
      if (error.response?.status === 404) {
        setError('Results not found. The link may be invalid or expired.');
      } else {
        setError('Failed to load results. Please try again later.');
      }
      toast.error('Failed to load interview results');
    } finally {
      setLoading(false);
    }
  };

  const getProctoringStatus = (value, thresholds) => {
    if (value >= thresholds.good) return 'good';
    if (value >= thresholds.warning) return 'warning';
    return 'critical';
  };

  const getResultBadge = (result) => {
    if (result === 'pass' || result === 'passed') {
      return <Badge className="bg-green-500/20 text-green-600 border-green-500/30">Passed</Badge>;
    } else if (result === 'fail' || result === 'failed') {
      return <Badge className="bg-red-500/20 text-red-600 border-red-500/30">Failed</Badge>;
    }
    return <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">Pending</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Results Unavailable</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link to="/">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const proctoring = interview?.proctoring_summary || {};
  const analysis = interview?.ai_analysis || {};
  const responses = interview?.responses || [];

  // Check if interview is under review - show special message
  if (interview?.status === 'under_review') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Header */}
        <header className="bg-white border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Interview Results</h1>
                <p className="text-gray-600">{interview?.university_name}</p>
              </div>
            </div>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <Card className="border-orange-500 bg-orange-50">
            <CardContent className="py-8 text-center">
              <Clock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Interview Under Review</h2>
              <p className="text-gray-600 mb-4">
                Hello <strong>{interview?.student_name}</strong>, your interview has been submitted successfully.
              </p>
              <p className="text-gray-600 mb-6">
                {interview?.message || "Your interview is currently being reviewed by our team. You will receive an email once the review is complete and your results are ready."}
              </p>
              <div className="bg-white rounded-lg p-4 border border-orange-200">
                <p className="text-sm text-gray-500">
                  <strong>Submitted:</strong> {new Date(interview?.completed_at || interview?.created_at).toLocaleDateString()}
                </p>
              </div>
              <Link to="/" className="mt-6 inline-block">
                <Button variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Return Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Helper to get proctoring value (handles both snake_case and camelCase)
  const getProctoring = (camelKey, snakeKey) => {
    return proctoring[camelKey] ?? proctoring[snakeKey] ?? 0;
  };

  // Calculate overall scores
  const aiScore = analysis?.overall_score || 
    Math.round((
      (analysis?.communication_score || 0) + 
      (analysis?.knowledge_score || 0) + 
      (analysis?.confidence_score || 0) + 
      (analysis?.integrity_score || 0)
    ) / 4) || 0;

  // Get proctoring/analytics values (handle both formats)
  const eyeContact = getProctoring('eyeContactPercent', 'eye_contact_percent');
  const faceMatch = getProctoring('faceMatchPercent', 'face_match_percent');
  const tabSwitches = getProctoring('tabSwitchCount', 'tab_switch_count');
  const fullscreenExits = getProctoring('fullscreen_exit_count', 'fullscreen_exit_count');
  const multipleFaces = getProctoring('multipleFacesCount', 'multiple_faces_count');
  const voiceActivity = getProctoring('voiceActivityPercent', 'voice_activity_percent');
  const faceAbsent = getProctoring('faceAbsentCount', 'face_absent_count');
  const faceSwapEvents = getProctoring('faceSwapEvents', 'face_swap_events');
  const multipleVoices = getProctoring('multipleVoicesCount', 'multiple_voices_count');

  const integrityScore = Math.round(
    ((eyeContact || 0) + 
     (faceMatch || 100) + 
     (100 - Math.min((tabSwitches || 0) * 20, 100)) +
     (100 - Math.min((fullscreenExits || 0) * 15, 100))) / 4
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Interview Results</h1>
              <p className="text-gray-600">{interview?.university_name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Completed on</p>
              <p className="font-medium">{new Date(interview?.completed_at || interview?.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Result Status Banner */}
        <Card className={`mb-6 ${interview?.result === 'pass' || interview?.result === 'passed' ? 'border-green-500 bg-green-50' : interview?.result === 'fail' || interview?.result === 'failed' ? 'border-red-500 bg-red-50' : 'border-yellow-500 bg-yellow-50'}`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {interview?.result === 'pass' || interview?.result === 'passed' ? (
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                ) : interview?.result === 'fail' || interview?.result === 'failed' ? (
                  <XCircle className="w-10 h-10 text-red-600" />
                ) : (
                  <Clock className="w-10 h-10 text-yellow-600" />
                )}
                <div>
                  <h2 className="text-xl font-bold">
                    {interview?.result === 'pass' || interview?.result === 'passed' ? 'Congratulations! You Passed' : 
                     interview?.result === 'fail' || interview?.result === 'failed' ? 'Interview Not Passed' : 
                     'Results Pending Review'}
                  </h2>
                  <p className="text-gray-600">
                    {interview?.result === 'pass' || interview?.result === 'passed' ? 'You have successfully completed the credibility interview.' :
                     interview?.result === 'fail' || interview?.result === 'failed' ? 'Please contact your agent for more information.' :
                     'Your interview is being reviewed.'}
                  </p>
                </div>
              </div>
              {getResultBadge(interview?.result)}
            </div>
          </CardContent>
        </Card>

        {/* Tabs - Now 3 tabs: Overview, Q&A Responses, AI Analysis & Recommendations */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="qa" data-testid="tab-qa">Q&A Responses</TabsTrigger>
            <TabsTrigger value="analysis" data-testid="tab-analysis">AI Analysis & Recommendations</TabsTrigger>
          </TabsList>

          {/* Overview Tab - Now includes Analytics (formerly Proctoring) */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Top Row - Student Info, AI Score, Integrity Score */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Student Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Student Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium">{interview?.student_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{interview?.student_email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">University</p>
                      <p className="font-medium">{interview?.university_name || 'N/A'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Score */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-5 h-5" />
                      AI Assessment Score
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex justify-center py-4">
                    <ScoreCircle score={aiScore} label="Overall" size="lg" />
                  </CardContent>
                </Card>

                {/* Integrity Score */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Integrity Score
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex justify-center py-4">
                    <div className={`relative w-28 h-28 rounded-full border-4 flex items-center justify-center ${
                      integrityScore >= 70 ? 'border-green-500' : integrityScore >= 50 ? 'border-yellow-500' : 'border-red-500'
                    }`}>
                      <div className="text-center">
                        <span className={`text-3xl font-bold ${
                          integrityScore >= 70 ? 'text-green-600' : integrityScore >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>{integrityScore}</span>
                        <p className="text-xs text-gray-500">Integrity</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Consent Recording */}
              {interview?.consent_recording_url && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Consent Recording
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="outline" 
                      onClick={() => window.open(interview.consent_recording_url, '_blank')}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Play Consent Recording
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Analytics Section (formerly Proctoring) */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Analytics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <AnalyticsCard
                      icon={EyeIcon}
                      title="Eye Contact"
                      value={eyeContact}
                      unit="%"
                      status={getProctoringStatus(eyeContact, { good: 70, warning: 50 })}
                      description="Percentage of time maintaining eye contact with camera"
                    />
                    
                    <AnalyticsCard
                      icon={ScanFace}
                      title="Face Match"
                      value={faceMatch}
                      unit="%"
                      status={getProctoringStatus(faceMatch, { good: 80, warning: 60 })}
                      description="Match with ID document photo"
                    />
                    
                    <AnalyticsCard
                      icon={MonitorX}
                      title="Tab Switches"
                      value={tabSwitches}
                      unit="times"
                      status={tabSwitches > 3 ? 'critical' : tabSwitches > 0 ? 'warning' : 'good'}
                      description="Number of times switched away from interview"
                    />
                    
                    <AnalyticsCard
                      icon={Target}
                      title="Fullscreen Exits"
                      value={fullscreenExits}
                      unit="times"
                      status={fullscreenExits > 2 ? 'critical' : fullscreenExits > 0 ? 'warning' : 'good'}
                      description="Number of times exited fullscreen mode"
                    />
                    
                    <AnalyticsCard
                      icon={Users}
                      title="Multiple Faces"
                      value={multipleFaces}
                      unit="times"
                      status={multipleFaces > 0 ? 'critical' : 'good'}
                      description="Instances where multiple faces were detected"
                    />
                    
                    <AnalyticsCard
                      icon={UserX}
                      title="Face Swap Events"
                      value={faceSwapEvents || 0}
                      unit="times"
                      status={faceSwapEvents > 3 ? 'critical' : faceSwapEvents > 0 ? 'warning' : 'good'}
                      description="Times when a different face was detected"
                    />
                    
                    <AnalyticsCard
                      icon={Volume2}
                      title="Multiple Voices"
                      value={multipleVoices || 0}
                      unit="times"
                      status={multipleVoices > 5 ? 'critical' : multipleVoices > 0 ? 'warning' : 'good'}
                      description="Different voice or multiple speakers detected"
                    />
                    
                    <AnalyticsCard
                      icon={Activity}
                      title="Voice Activity"
                      value={voiceActivity}
                      unit="%"
                      status={getProctoringStatus(voiceActivity, { good: 30, warning: 10 })}
                      description="Percentage of time speaking during answers"
                    />
                  </div>
                  
                  {/* Face Absent Warning */}
                  {faceAbsent > 0 && (
                    <div className="mt-4 p-4 rounded-lg border border-yellow-500 bg-yellow-50">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-8 h-8 text-yellow-600" />
                        <div>
                          <p className="font-medium text-yellow-800">Face Not Detected: {faceAbsent} times</p>
                          <p className="text-sm text-yellow-700">Your face was not visible to the camera during parts of the interview.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Q&A Responses Tab - No transcripts, only Question, Result, Score, Video */}
          <TabsContent value="qa">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Questions & Responses ({responses.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {responses.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No responses recorded</p>
                ) : (
                  <div className="space-y-4">
                    {responses.map((response, index) => {
                      // Normalize score: if > 10, assume it's a percentage and convert to 0-10 scale
                      let displayScore = response.score;
                      if (displayScore !== undefined && displayScore !== null) {
                        if (displayScore > 10) {
                          displayScore = Math.round(displayScore / 10); // Convert percentage to 0-10
                        } else {
                          displayScore = Math.round(displayScore * 10) / 10; // Round to 1 decimal
                        }
                      }
                      
                      const passed = response.passed || (displayScore !== undefined && displayScore >= 5);
                      
                      return (
                        <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                                  Q{index + 1}
                                </span>
                                {/* Result Badge */}
                                {passed ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Passed
                                  </Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700 border-red-300">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Needs Improvement
                                  </Badge>
                                )}
                                {/* Score Badge */}
                                {displayScore !== undefined && displayScore !== null && (
                                  <Badge className={`${
                                    displayScore >= 7 ? 'bg-green-100 text-green-700' :
                                    displayScore >= 5 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    Score: {displayScore}/10
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Question Text */}
                              <p className="text-gray-800 font-medium">
                                {response.question_text || response.question || 'Question not recorded'}
                              </p>
                              
                              {/* Improvement Tip for Failed Questions */}
                              {!passed && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <div className="flex items-start gap-2">
                                    <Target className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <p className="text-sm font-medium text-blue-700">Tip for Improvement:</p>
                                      <p className="text-sm text-blue-600 mt-1">
                                        {response.expected_keywords && response.expected_keywords.length > 0 ? (
                                          <>Try to include key points like: <span className="font-medium">{response.expected_keywords.slice(0, 5).join(', ')}</span>{response.expected_keywords.length > 5 ? '...' : ''}</>
                                        ) : (response.question_text || response.question || '').toLowerCase().includes('cultural') || (response.question_text || response.question || '').toLowerCase().includes('experience') ? (
                                          'Mention specific activities, events, traditions, or experiences you are looking forward to exploring.'
                                        ) : (response.question_text || response.question || '').toLowerCase().includes('backup') || (response.question_text || response.question || '').toLowerCase().includes('emergency') || (response.question_text || response.question || '').toLowerCase().includes('plan') ? (
                                          'Discuss contingency plans, emergency contacts, financial backup, and practical preparations you have made.'
                                        ) : (response.question_text || response.question || '').toLowerCase().includes('university') || (response.question_text || response.question || '').toLowerCase().includes('compare') || (response.question_text || response.question || '').toLowerCase().includes('institution') ? (
                                          'Highlight unique programs, rankings, facilities, research opportunities, or career support that sets your choice apart.'
                                        ) : (response.question_text || response.question || '').toLowerCase().includes('why') ? (
                                          'Provide specific reasons with personal motivation and concrete examples to support your answer.'
                                        ) : (response.question_text || response.question || '').toLowerCase().includes('how') ? (
                                          'Explain your approach, process, or steps clearly with practical details and examples.'
                                        ) : (response.question_text || response.question || '').toLowerCase().includes('hope') || (response.question_text || response.question || '').toLowerCase().includes('gain') || (response.question_text || response.question || '').toLowerCase().includes('expect') ? (
                                          'Share specific goals, skills you want to develop, and how this opportunity aligns with your aspirations.'
                                        ) : (response.question_text || response.question || '').toLowerCase().includes('finance') || (response.question_text || response.question || '').toLowerCase().includes('fund') || (response.question_text || response.question || '').toLowerCase().includes('pay') ? (
                                          'Explain your funding sources, financial planning, and how you will manage expenses during your studies.'
                                        ) : (response.question_text || response.question || '').toLowerCase().includes('career') || (response.question_text || response.question || '').toLowerCase().includes('job') || (response.question_text || response.question || '').toLowerCase().includes('work') ? (
                                          'Discuss your career goals, relevant skills, and how this opportunity will help you achieve them.'
                                        ) : (
                                          'Provide detailed, specific answers with relevant examples to strengthen your response.'
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Video Download Button */}
                            <div className="flex-shrink-0">
                              {response.video_url ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => downloadVideo(response.video_url, `Q${index + 1}_recording.webm`)}
                                  className="flex items-center gap-2 hover:bg-lime-50 hover:border-lime-500 hover:text-lime-700"
                                >
                                  <Download className="w-4 h-4" />
                                  Download
                                </Button>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  disabled
                                  className="flex items-center gap-2 opacity-50"
                                >
                                  <Video className="w-4 h-4" />
                                  No Recording
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Analysis & Recommendations Tab */}
          <TabsContent value="analysis">
            {analysis && Object.keys(analysis).length > 0 ? (
              <div className="space-y-6">
                {/* Score Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Score Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <ScoreCircle score={analysis.communication_score || 0} label="Communication" size="sm" />
                      <ScoreCircle score={analysis.knowledge_score || 0} label="Knowledge" size="sm" />
                      <ScoreCircle score={analysis.confidence_score || 0} label="Confidence" size="sm" />
                      <ScoreCircle score={analysis.integrity_score || 0} label="Integrity" size="sm" />
                    </div>
                  </CardContent>
                </Card>

                {/* Final Verdict - Show prominently when admin reviewed */}
                {interview?.admin_reviewed && interview?.result && (
                  <Card className={`border-2 ${
                    interview.result === 'pass' || interview.result === 'passed'
                      ? 'border-green-500 bg-green-50'
                      : 'border-red-500 bg-red-50'
                  }`}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {interview.result === 'pass' || interview.result === 'passed' ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        Final Verdict (Admin Reviewed)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={`text-xl font-bold ${
                        interview.result === 'pass' || interview.result === 'passed'
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {interview.result?.toUpperCase()}
                      </p>
                      {(interview.result === 'pass' || interview.result === 'passed') && 
                       analysis.verdict?.toLowerCase().includes('fail') && (
                        <p className="text-sm text-green-700 mt-2">
                          Note: The AI flagged some concerns during automatic assessment, but after careful manual review by our team, your interview has been approved.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* AI Summary */}
                {analysis.summary && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="w-5 h-5" />
                        AI Summary {interview?.admin_reviewed && '(Initial Assessment)'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700">{analysis.summary}</p>
                      {interview?.admin_reviewed && (interview.result === 'pass' || interview.result === 'passed') &&
                       analysis.verdict?.toLowerCase().includes('fail') && (
                        <p className="text-xs text-gray-500 mt-2 italic">
                          This was the AI's initial assessment before manual review.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Strengths */}
                {analysis.strengths && analysis.strengths.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-green-600">
                        <Star className="w-5 h-5" />
                        Strengths
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {analysis.strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-green-700">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Areas for Improvement */}
                {analysis.weaknesses && analysis.weaknesses.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-yellow-600">
                        <AlertTriangle className="w-5 h-5" />
                        Areas for Improvement
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {analysis.weaknesses.map((weakness, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-yellow-700">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                {analysis.recommendations && analysis.recommendations.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-blue-600">
                        <Target className="w-5 h-5" />
                        Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {analysis.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-blue-700">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">AI analysis not yet available for this interview.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} VisaInterviews.ai - AI-Powered Interview Preparation</p>
        </div>
      </footer>
    </div>
  );
}
